import { useEffect, useState } from "react";

interface SharePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export default function SharePanel({ open, onClose, title }: SharePanelProps) {
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl(window.location.href);
      setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
    }
    window.setTimeout(() => setCopied(false), 2200);
  };

  const openShare = (target: "weibo" | "x") => {
    const shareUrl =
      target === "weibo"
        ? `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
        : `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground-950/60 p-0 sm:p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="分享面板"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-background-50 rounded-t-lg sm:rounded-lg border border-background-200 animate-slide-up sm:animate-fade-up"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <h2 className="text-base font-bold text-foreground-950">分享这篇内容</h2>
          <button
            type="button"
            aria-label="关闭分享面板"
            onClick={onClose}
            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-4">
          <p className="text-xs text-foreground-500 mb-2">页面链接</p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={url}
              aria-label="当前页面链接"
              className="flex-1 min-w-0 h-10 px-3 text-xs rounded-md border border-background-200 bg-background-100 text-foreground-700"
            />
            <button
              type="button"
              onClick={copyLink}
              className={`h-10 px-4 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                copied
                  ? "bg-secondary-500 text-background-50"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              {copied ? "已复制" : "复制链接"}
            </button>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2.5">
            <button
              type="button"
              onClick={copyLink}
              className="flex flex-col items-center gap-2 py-3 rounded-md border border-background-200 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-wechat-line text-xl text-primary-600" />
              <span className="text-xs text-foreground-700">微信</span>
            </button>
            <button
              type="button"
              onClick={() => openShare("weibo")}
              className="flex flex-col items-center gap-2 py-3 rounded-md border border-background-200 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-weibo-line text-xl text-accent-600" />
              <span className="text-xs text-foreground-700">微博</span>
            </button>
            <button
              type="button"
              onClick={() => openShare("x")}
              className="flex flex-col items-center gap-2 py-3 rounded-md border border-background-200 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-twitter-x-line text-xl text-foreground-800" />
              <span className="text-xs text-foreground-700">X</span>
            </button>
          </div>

          <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
            微信分享会先复制链接，你可以粘贴给好友。社交平台分享为演示入口。
          </p>
        </div>
      </div>
    </div>
  );
}